import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from '@/utils/icons';
import { formatPhone, isPhoneValid, phoneDigits } from '@/utils/phone';
import { updatePhone, recordSmsConsent } from '@/services/phoneSmsPreferences';

const SMS_DISCLOSURE =
  'I agree to receive SMS training reminders from CGM Sports. ' +
  'Message frequency varies. Msg & data rates may apply. Reply HELP for help, ' +
  'STOP to unsubscribe. We will never share your phone number with third parties.';

const DISMISS_KEY_PREFIX = 'phone_prompt_dismissed_';

function dismissKeyForUser(userId: number | string): string {
  return `${DISMISS_KEY_PREFIX}${userId}`;
}

interface PhonePromptModalProps {
  userId: number;
  onClose: () => void;
}

export const shouldShowPhonePrompt = (): boolean => {
  try {
    const raw = localStorage.getItem('user_info');
    if (!raw) return false;
    const user = JSON.parse(raw);
    if (!user.id) return false;
    if (user.phone) return false;
    if (localStorage.getItem(dismissKeyForUser(user.id))) return false;
    return true;
  } catch {
    return false;
  }
};

const PhonePromptModal: React.FC<PhonePromptModalProps> = ({ userId, onClose }) => {
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(dismissKeyForUser(userId), '1');
    setDismissed(true);
    setTimeout(onClose, 2400);
  };

  const handleSave = async () => {
    setError(null);
    if (!isPhoneValid(phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setSaving(true);
    try {
      const digits = phoneDigits(phone);
      await updatePhone(digits);

      if (smsOptIn) {
        await recordSmsConsent(userId, 'granted', SMS_DISCLOSURE);
      }

      const stored = localStorage.getItem('user_info');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.phone = digits;
        parsed.sms_opt_in = smsOptIn;
        localStorage.setItem('user_info', JSON.stringify(parsed));
      }
      window.dispatchEvent(new Event('user-info-changed'));

      localStorage.setItem(dismissKeyForUser(userId), '1');
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="cgm-card rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 relative">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-4 right-4 cgm-text-muted hover:cgm-text-primary"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {dismissed ? (
          <div className="text-center py-6">
            <p className="cgm-text-secondary text-sm">
              No problem — you can add your phone number in{' '}
              <span className="font-semibold cgm-text-primary">Settings</span> any time.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold cgm-text-primary mb-1">
              Stay in the loop
            </h2>
            <p className="cgm-text-secondary text-sm mb-5">
              Add your phone number to receive real-time text alerts when athletes need attention.
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="prompt-phone" className="block text-sm font-medium cgm-text-secondary mb-1">
                  Phone Number
                </label>
                <input
                  id="prompt-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(formatPhone(e.target.value)); setError(null); }}
                  disabled={saving}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50"
                  placeholder="(555) 123-4567"
                  autoFocus
                />
              </div>

              {phone && (
                <label htmlFor="prompt-sms-opt-in" className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    id="prompt-sms-opt-in"
                    type="checkbox"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    disabled={saving}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-emerald-600"
                  />
                  <span className="text-xs cgm-text-muted leading-relaxed">
                    {SMS_DISCLOSURE} See{' '}
                    <Link to="/privacy" className="underline">Privacy Policy</Link> and{' '}
                    <Link to="/terms" className="underline">Terms</Link>.
                  </span>
                </label>
              )}

              {error && (
                <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="text-sm cgm-text-muted hover:cgm-text-secondary cursor-pointer"
                >
                  Skip for now
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !phone}
                  className="px-5 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 rounded-lg font-medium text-sm disabled:opacity-50 cursor-pointer"
                >
                  {saving ? 'Saving…' : 'Save & Enable Alerts'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhonePromptModal;
